import React from "react";
import IntegrationConversionSettings from "./IntegrationConversionSettings";
import {IntegrationAdAccountList, IntegrationAdAccountRow} from "./IntegrationAdAccounts";
import {Box, Button, DialogActions, DialogContent, List, ListItem, StepConnector, TextField, useMediaQuery, useTheme, ListItemIcon, ListItemText, Step, StepLabel, Stepper, Link, Typography} from "@mui/material";
import {IntegrationDialog as Dialog, IntegrationHeader, IntegrationBackButton} from "./IntegrationDesign";
import {integrationActions} from "../../../store/actions/integrationAction";
import {connect} from "react-redux";
import {useCallback, useEffect, useState} from "react";
import Spinner from "../../spinner/Spinner";
import {Check} from "@mui/icons-material";
import {useFieldArray, useForm} from "react-hook-form";
import {alertActions} from "../../../store/actions/alertActions";
import useCustomStyles from "../../utils/UseCustomStyles";

const customStyles = (theme) => ({
    chooseAccount: {
        fontWeight: 500,
        fontSize: 14,
        paddingTop: 24,
        paddingBottom: 4
    },
    listItem: {
        paddingTop: 0,
        paddingLeft: 0,
        paddingBottom: 0
    },
    listItemIcon: {
        minWidth: 42
    },
    stepper: {
        margin: "0 auto",
        width: "100%",
        maxWidth: 800,
        padding: "8px 24px 24px 24px"
    },
    stepConnectorLine: {
        borderColor: theme.palette.background.tableBorder
    },
    helperText: {
        color: theme.palette.error.main,
    },
});

function GoogleAdAccountIntegration(props) {
    const theme = useTheme();
    const classes = useCustomStyles(customStyles, theme);
    const {
        integration,
        getGoogleAdAccounts,
        integrateGoogleAdAccount,
        resetIntegration,
        integrateGoogleConversionAPI,
        removeIntegrationEvent
    } = props;

    const steps = ['Connect', 'Integrate Account', 'Conversion API'];

    const integrationId = parseInt(props?.match?.params?.id);

    const [open, setOpen] = useState(true);
    const [activeStep, setActiveStep] = useState(1);
    const [removeEventIndex, setRemoveEventIndex] = useState(null);

    const {register, handleSubmit, setValue, control, watch, getValues, formState: {errors}} = useForm();
    const {fields, remove, append} = useFieldArray({
        control,
        name: "events"
    });

    const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));

    const handleClose = useCallback((event, reason) => {
        if (reason === 'backdropClick') {
            return false;
        }

        resetIntegration();
        setOpen(false);
        props.history.push({pathname: "/settings/integrations", state: {from: props.location}});
    }, [resetIntegration, props]);

    const handleCustomerIdFormat = (s) => {
        return s.replace(/(\d{3})(\d{3})(\d{4})/, "$1-$2-$3");
    };

    const handleOnSubmit = (data) => {
        data.integrationId = integrationId;
        integrateGoogleConversionAPI(data);
        handleClose();
    };

    const appendNewEvent = () => {
        append({
            eventId: 0,
            name: "",
            includePayout: false
        });
    };

    const handleDeleteIntegrationEvent = useCallback(({eventId, index}) => {
        if (eventId === 0) {
            remove(index); // from list
            return false;
        }
        setRemoveEventIndex({eventId, index}); // set event to remove from list and do API req.
    }, [remove, setRemoveEventIndex]);

    useEffect(() => {
        if (removeEventIndex !== null) {
            remove(removeEventIndex?.index); // from list
            removeIntegrationEvent({integrationId, eventId: removeEventIndex?.eventId}); // API req
        }
    }, [integrationId, remove, removeEventIndex, removeIntegrationEvent]);

    useEffect(() => {
        if (integration.adAccounts === null) {
            getGoogleAdAccounts(integrationId);
        }
    }, [integrationId, getGoogleAdAccounts, integration.adAccounts])

    useEffect(() => {
        if (activeStep === 2 && integration?.integrations !== null) {
            for (const i of integration?.integrations) {
                if (i?.id === integrationId) {
                    for (const cred of i?.integrationCredentials) {
                        if (cred?.keyName === "google_enable_conversion_api") {
                            if (cred?.keyValue === "true") {
                                setValue("enableConversionAPI", true);
                            } else {
                                setValue("enableConversionAPI", false);
                            }
                        }
                        if (cred?.keyName === "google_mcc_ad_account_id") {
                            setValue("mccAdAccountId", cred?.keyValue);
                        }
                    }

                    if (getValues("events")?.length === 0) {
                        let events = [];
                        i?.integrationEvents?.forEach((event) => {
                            events.push({
                                eventId: event?.id,
                                name: event?.name,
                                includePayout: event?.includePayout
                            });
                        });
                        setValue("events", events);
                    }

                    break;
                }
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeStep])

    return (
        <Dialog open={open}
                onClose={handleClose}
                fullWidth={true}
                fullScreen={fullScreen}
                maxWidth={"sm"}
                aria-labelledby="facebook-ad-account-integration"
        >
            <IntegrationHeader name="Google Ads" logo="/assets/images/traffic-sources/googleadswhite.svg" id="facebook-ad-account-integration"/>
            <DialogContent>

                <Stepper
                    alternativeLabel={fullScreen}
                    activeStep={activeStep}
                    connector={<StepConnector classes={{line: classes?.stepConnectorLine}}/>}
                    classes={{root: classes?.stepper}}
                >
                    {steps.map((label) => (
                        <Step key={label}>
                            <StepLabel>{label}</StepLabel>
                        </Step>
                    ))}
                </Stepper>

                {activeStep === 1 &&
                    <>
                        <List>
                            <ListItem className={classes?.listItem}>
                                <ListItemIcon className={classes?.listItemIcon}>
                                    <Check color={"primary"}/>
                                </ListItemIcon>
                                <ListItemText disableTypography primary="Auto-matching for all campaigns."/>
                            </ListItem>
                            <ListItem className={classes?.listItem}>
                                <ListItemIcon className={classes?.listItemIcon}>
                                    <Check color={"primary"}/>
                                </ListItemIcon>
                                <ListItemText disableTypography primary="Synchronize campaign cost every 30 minutes."/>
                            </ListItem>
                        </List>
                        <p>
                            Please check our integration
                            guide <Link target="_blank" underline="hover" rel="noreferrer"
                                        href="https://skro.eu/docs/integrations/google-ads-integration"
                        >
                            here
                        </Link>.
                        </p>
                        <div className={classes?.chooseAccount}>Google Ad Accounts</div>
                        {
                            integration?.loading?.adAccounts ? (
                                <Spinner overComponentBox={true}/>
                            ) : (
                                <IntegrationAdAccountList provider="Google Ads">
                                    {integration?.adAccounts?.map((item) => {
                                        return (
                                            <IntegrationAdAccountRow key={item?.customerId} name={item?.descriptiveName}
                                                accountId={handleCustomerIdFormat(item?.customerId)} integrated={item?.integrated}
                                                onIntegrate={() => integrateGoogleAdAccount({
                                                            adAccountId: item?.customerId,
                                                            adAccountName: item?.descriptiveName,
                                                            adAccountTimezone: item?.timezone,
                                                            adAccountCurrencyCode: item?.currencyCode,
                                                            integrationId: integrationId
                                                        })}/>
                                        );
                                    })}
                                </IntegrationAdAccountList>
                            )
                        }
                    </>
                }

                {activeStep === 2 &&
                    <Box sx={{position: "relative"}}>
                        {(integration?.loading?.conversionApi || integration?.loading?.integration) &&
                            <Spinner overComponentBox={true}/>}
                        <IntegrationConversionSettings provider="Google Ads" control={control} register={register} errors={errors}
                            enabled={watch("enableConversionAPI")} eventFields={fields}
                            onAddEvent={appendNewEvent} onDeleteEvent={handleDeleteIntegrationEvent}
                            eventLabel="Conversion Action Name" eventPlaceholder="Enter Google Ads conversion action name">
                            <Box sx={{display: "grid", gap: 1.5, minWidth: 0}}>
                                <TextField
                                    error={!!errors?.mccAdAccountId}
                                    helperText={errors?.mccAdAccountId?.message}
                                    variant="outlined"
                                    margin="dense"
                                    type="text"
                                    size="small"
                                    placeholder="Google MCC Ad Account ID (Optional)"
                                    fullWidth
                                    label="Google MCC Ad Account ID (Optional)"
                                    sx={theme => theme.palette.mode === "light" ? {
                                        "&&& .MuiOutlinedInput-root": {bgcolor: "background.paper"}
                                    } : {}}
                                    slotProps={{
                                        inputLabel: {
                                            shrink: true
                                        }
                                    }}
                                    name={`mccAdAccountId`}
                                    defaultValue=""
                                    {...register("mccAdAccountId", {
                                        maxLength: {
                                            value: 15,
                                            message: "Max length is 15 numbers"
                                        },
                                        pattern: {
                                            value: /^[0-9]+$/,
                                            message: "Only numbers are allowed"
                                        }
                                    })}
                                    onInput={(e) => {
                                        e.target.value = e?.target?.value?.replace(/\D/g, "");
                                    }}
                                />
                                <Typography sx={{fontSize: 12, color: "text.secondary"}}>
                                    Enter an MCC ID only to send conversions to that manager account instead of the ad account.
                                    The Google account used for this integration must have access to both accounts.
                                </Typography>
                            </Box>
                        </IntegrationConversionSettings>
                    </Box>
                }
            </DialogContent>
            <DialogActions>
                <Box display="flex" justifyContent="flex-end">
                    <Box mr={3}>
                        <Button onClick={handleClose} color="inherit" variant="text">
                            Close
                        </Button>
                    </Box>
                    {activeStep === 1 &&
                        <Box>
                            <Button onClick={() => setActiveStep(2)} color="primary" variant="contained">
                                Next
                            </Button>
                        </Box>
                    }
                    {activeStep === 2 &&
                        <>
                            <Box mr={1}>
                                <IntegrationBackButton onClick={() => setActiveStep(1)}/>
                            </Box>
                            <Box>
                                <Button onClick={handleSubmit(handleOnSubmit)} color="primary" variant="contained">
                                    Save & Close
                                </Button>
                            </Box>
                        </>
                    }
                </Box>
            </DialogActions>
        </Dialog>
    );
}

const mapStateToProps = (state) => {
    return {
        integration: state.integration,
        alert: state.alert
    };
};

const mapDispatchToProps = {
    resetIntegration: integrationActions.reset,
    getGoogleAdAccounts: integrationActions.getGoogleAdAccounts,
    integrateGoogleAdAccount: integrationActions.integrateGoogleAdAccount,
    integrateGoogleConversionAPI: integrationActions.integrateGoogleConversionAPI,
    error: alertActions.error,
    removeIntegrationEvent: integrationActions.removeIntegrationEventFromCurrentIntegration
};

export default connect(mapStateToProps, mapDispatchToProps)(GoogleAdAccountIntegration);
